import { useEffect, useRef, useState } from 'react';
import type { CSSProperties } from 'react';
import type { PDFDocumentProxy } from 'pdfjs-dist';

export interface SlidePageCanvasProps {
  doc: PDFDocumentProxy;
  /** 1-based PDF page number. */
  pageNum: number;
  /**
   * Native render width in CSS px. The canvas backing store is rendered at
   * `width × devicePixelRatio`; CSS caps the displayed size at `width` but lets
   * it shrink to fit narrower containers.
   */
  width: number;
  /** Extra styles merged onto the <canvas>. */
  style?: CSSProperties;
  ariaLabel?: string;
}

/**
 * Renders a single PDF page to a <canvas> via PDF.js.
 *
 * Extracted from the byte-identical thumbnail renderers in SlideDeckPreview
 * (MED-784) and SectionVisualsPicker (MED-975) so the live-session whiteboard
 * and participant slide card (E3.3a) render authored slides through one path.
 *
 * Responsive: fills its container width but never upscales past `width` (its
 * native render resolution), and renders at devicePixelRatio for crisp output.
 */
export function SlidePageCanvas({ doc, pageNum, width, style, ariaLabel }: SlidePageCanvasProps) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const [rendered, setRendered] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setRendered(false);
    void (async () => {
      const page = await doc.getPage(pageNum);
      if (cancelled || !canvasRef.current) return;
      const baseViewport = page.getViewport({ scale: 1 });
      const scale = width / baseViewport.width;
      const viewport = page.getViewport({ scale });
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');
      if (!ctx) return;
      // High-DPI rendering — draw at devicePixelRatio for crisp output.
      const dpr = window.devicePixelRatio || 1;
      canvas.width = Math.ceil(viewport.width * dpr);
      canvas.height = Math.ceil(viewport.height * dpr);
      canvas.style.width = '100%';
      canvas.style.maxWidth = `${viewport.width}px`;
      canvas.style.height = 'auto';
      ctx.scale(dpr, dpr);
      await page.render({ canvasContext: ctx, viewport, canvas }).promise;
      if (!cancelled) setRendered(true);
    })().catch(() => {
      // best-effort render — a failed page leaves the canvas blank.
    });
    return () => {
      cancelled = true;
    };
  }, [doc, pageNum, width]);

  return (
    <canvas
      ref={canvasRef}
      aria-label={ariaLabel}
      style={{
        display: 'block',
        opacity: rendered ? 1 : 0.3,
        transition: 'opacity 200ms',
        ...style,
      }}
    />
  );
}
